/**
 * Single source of truth for subscription / trial / plan limits.
 * UI plan cards still live in BillingConfig.js (visual features);
 * numeric limits and trial defaults are defined here and re-exported there.
 */

export const TRIAL_DURATION_DAYS = 30;

export const SUBSCRIPTION_STATUS = {
  TRIAL: "trial",
  ACTIVE: "active",
  EXPIRED: "expired",
};

export const BILLING_CYCLE = {
  MONTHLY: "monthly",
  YEARLY: "yearly",
};

/** Canonical free trial plan (Community Backer). */
export const FREE_TRIAL_PLAN = {
  planId: "free",
  tier: "free",
  tierName: "Community Backer",
  formLimit: 1,
  billingCycle: BILLING_CYCLE.MONTHLY,
  price: 0,
  status: SUBSCRIPTION_STATUS.TRIAL,
  autoRenew: false,
  isSponsorship: false,
};

/** Paid / free plan form limits (must match BillingConfig PLANS). */
export const PLAN_FORM_LIMITS = {
  free: 1,
  silver: 2,
  gold: 5,
  diamond: 10,
};

export const getFormLimitForPlanId = (planId) =>
  PLAN_FORM_LIMITS[planId] ?? PLAN_FORM_LIMITS.free;

/**
 * Build the default free-trial subscription object for a new company.
 * @param {Date} [now]
 */
export const createFreeTrialSubscription = (now = new Date()) => {
  const start = new Date(now);
  const expiresAt = new Date(
    start.getTime() + TRIAL_DURATION_DAYS * 24 * 60 * 60 * 1000
  );
  return {
    ...FREE_TRIAL_PLAN,
    startDate: start.toISOString(),
    expiresAt: expiresAt.toISOString(),
  };
};

/**
 * Add billing-cycle duration to a base date.
 * Monthly = +30 days; Yearly = +1 calendar year (paid plans only).
 */
export const calculateSubscriptionExpiry = (
  billingCycle,
  startDate = new Date()
) => {
  const date = new Date(startDate);
  if (billingCycle === BILLING_CYCLE.YEARLY) {
    date.setFullYear(date.getFullYear() + 1);
  } else {
    date.setDate(date.getDate() + TRIAL_DURATION_DAYS);
  }
  return date.toISOString();
};

export const calculatePlanPrice = (basePrice, billingCycle) =>
  billingCycle === BILLING_CYCLE.YEARLY ? basePrice * 10 : basePrice;

export const isSubscriptionExpired = (subscription) => {
  const expiresAt = subscription?.expiresAt;
  if (!expiresAt) return false;
  return new Date(expiresAt) < new Date();
};

export const getSubscriptionFormLimit = (subscription) => {
  const limit = parseInt(subscription?.formLimit, 10);
  if (Number.isFinite(limit) && limit > 0) return limit;
  return getFormLimitForPlanId(subscription?.planId || "free");
};
